import React, { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import TimeRangeDropdown from "../../time-range-filter/time-range-dropdown";
import "./profit-loss-summary.css";
import SimpleSpinner from "../../../components/loader";
import EmptyState from "../../../newComponents/emptystate";
import analyticsIcon from "../../../assets/analyticsIcon.svg";
import { GetImages } from "../../../utils/GetImages";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";

interface ProfitLossData {
  month: string;
  profit: number;
  loss: number;
}

interface ProfitLossSummaryProps {
  data: ProfitLossData[];
  timeRanges?: string[];
  dateFilter?: string;
  onTimeRangeChange?: (newRange: string) => void;
  isLoading?: boolean;
}

const CustomTooltip = ({
  active,
  payload,
  currencySymbol,
}: {
  active?: boolean;
  payload?: any;
  currencySymbol: string;
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const value = data.profit > 0 ? data.profit : data.loss;
    const isProfit = data.profit > 0;

    const formattedAmount = `${currencySymbol}${Math.abs(value).toLocaleString("en-US", {
      maximumFractionDigits: 0,
    })}`;

    return (
      <div className="chart-tooltip">
        <div className="tooltip-content">
          {isProfit ? "+" : ""}
          {formattedAmount}
        </div>
        <div className="tooltip-arrow"></div>
      </div>
    );
  }

  return null;
};

// Helper function to convert month number to short name
const getMonthName = (monthNum: number): string => {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return months[monthNum - 1] || "";
};

const ProfitLossSummary: React.FC<ProfitLossSummaryProps> = ({
  data,
  timeRanges = ["Last 12 Month", "Last 6 Month", "Last 3 Month", "This Month"],
  dateFilter = "Last 12 Month",
  onTimeRangeChange,
  isLoading = false,
}) => {
  const currencySymbol = useCurrencySymbol();
  const [selectedRange, setSelectedRange] = useState(dateFilter);

  const handleTimeRangeChange = (newRange: string) => {
    setSelectedRange(newRange);
    onTimeRangeChange?.(newRange);
  };


  const formatYAxis = (value: number) => {
    if (value === 0) return `${currencySymbol}0`;
    const absValue = Math.abs(value);
    const sign = value < 0 ? "-" : "";
    if (absValue >= 1000) {
      return `${currencySymbol}${sign}${(absValue / 1000).toFixed(0)}K`;
    }
    return `${currencySymbol}${sign}${absValue}`;
  };

  // Transform and fill data for all 12 months
  const { chartData, yAxisConfig } = useMemo(() => {
    const allMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    
    // Create a map of existing data by month name
    const dataMap = new Map<string, { profit: number; loss: number }>();
    
    data.forEach((item) => {
      const [monthStr] = item.month.split("/");
      const monthNum = parseInt(monthStr);
      const monthName = getMonthName(monthNum);
      
      if (monthName) {
        dataMap.set(monthName, {
          profit: item.profit || 0,
          loss: item.loss > 0 ? -item.loss : item.loss || 0,
        });
      }
    });

    // Create full 12 months data with 0 for missing months
    const fullYearData = allMonths.map((month) => {
      const existingData = dataMap.get(month);
      return {
        month,
        profit: existingData?.profit || 0,
        loss: existingData?.loss || 0,
      };
    });

    // Calculate dynamic Y-axis range
    let maxProfit = 0;
    let maxLoss = 0;

    fullYearData.forEach((item) => {
      if (item.profit > maxProfit) maxProfit = item.profit;
      if (Math.abs(item.loss) > maxLoss) maxLoss = Math.abs(item.loss);
    });

    // Get the maximum absolute value
    const maxValue = Math.max(maxProfit, maxLoss);
    
    // Round up to nearest 10K for cleaner axis
    const roundedMax = Math.ceil(maxValue / 10000) * 10000;
    const axisMax = roundedMax || 40000; // Default to 40K if no data

    // Generate ticks dynamically
    const tickInterval = axisMax / 4;
    const ticks = [];
    for (let i = -axisMax; i <= axisMax; i += tickInterval) {
      ticks.push(i);
    }

    return {
      chartData: fullYearData,
      yAxisConfig: {
        domain: [-axisMax, axisMax],
        ticks,
      },
    };
  }, [data]);

  return (
    <div className="profit-loss-summary">
      <div className="profit-loss-header">
        <h2 className="profit-loss-title">Profit Loss Summary</h2>
        <div className="flex gap-4 items-center">
          <TimeRangeDropdown
            timeRanges={timeRanges}
            defaultTimeRange={selectedRange}
            onTimeRangeChange={handleTimeRangeChange}
            className="my-custom-dropdown"
          />
        </div>
      </div>

      <div className="profit-loss-content">
        {/* Show loading spinner */}
        {isLoading ? (
          <div className="loading-spinner text-center py-4">
            <SimpleSpinner myClassName="Icon50 mt-5" />
          </div>
        ) : data.length === 0 ? (
          // Show empty state
          <EmptyState
            icon={GetImages.emptyStateData}
            className="w-100"
            isBorder={false}
            heading="No Data Found"
            isButton={false}
          />
        ) : (
          // Show chart
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 20, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--Grey-Divider)" vertical={true} />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "var(--Grey-Subtext2)" }}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: "var(--Grey-Subtext2)" }}
                tickFormatter={formatYAxis}
                domain={yAxisConfig.domain}
                ticks={yAxisConfig.ticks}
                dx={-10}
              />
              <Tooltip content={<CustomTooltip currencySymbol={currencySymbol} />} cursor={{ fill: "transparent" }} />
              <ReferenceLine y={0} stroke="var(--Grey-Divider)" strokeWidth={0.5} />
              <Bar
                dataKey="profit"
                fill="#2563eb"
                radius={[4, 4, 0, 0]}
                maxBarSize={60}
              />
              <Bar
                dataKey="loss"
                fill="#f97316"
                radius={[4, 4, 0, 0]}
                maxBarSize={60}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default ProfitLossSummary;